import React, {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "next/navigation";

import {
  Filter,
  FILTER_CONFIG_OPTIONS,
  GetMenuItemsV3Params,
} from "@/api/requests/consumerApi/getMenuItemsV3";
import { Action, Category } from "@/constants/events";
import { Coordinates } from "@/custom-types/Coordinates";
import { MenuItemCard } from "@/custom-types/MenuItemCard";
import useClearQueryStringKeys from "@/hooks/useClearQueryStringKeys";
import { useEventTracker } from "@/hooks/useEventTracker";
import { useSelector } from "@/redux/hooks";
import { getIsValidStreetAddress } from "@/redux/selectors/location";
import { mapFilterObjectToAPIV2 } from "@/utils/mapUIFiltersToAPI";

import AddressCaptureModal from "@/components/AddressCaptureModal";
import { Facet } from "@/components/FilteredGrid";
import useMenuFilterSearch from "@/components/MenuFilters/hooks/useMenuFilterSearch";
import { MenuPage } from "@/components/MenuFilters/types/MenuFilters";
import MenuFilters from "@/components/MenuFilters/v2";
import { useFilterQueryParams } from "@/components/MenuFilters/v2/hooks";
import MenuItemResults from "@/components/ShopCoreMenuItemResults/MenuItemResults";

import { SortState } from "../ShopCoreSort/ShopCoreSort";

//added to params (not filters) for each menu_item search when fulfillment is delivery
const DELIVERY_PARAMS = {
  decorate_with_user_fulfillment: true,
};

//added to filters param for each menu_item search
const DEFAULT_FILTERS = {};

//added to filters param for each menu_item search when fulfillment is pickup
const PICKUP_FILTERS = {
  max_distance: "20",
  pickup_available: true,
};

const DeliveryMenu = ({
  brandId,
  brandName,
  coordinates: { lat, lon },
  filterConfig,
  fulfillmentLoading,
  fulfillmentModeUnavailable,
  isDeliveryFulfillmentMode,
  outerClassNames = "",
  permanentFilters,
  renderPreFilteredView,
  renderNoResults,
  isFollowing,
  isPickupFulfillmentMode,
  mobileClassName,
  showEmptyResultsMessaging = true,
  menuPage = MenuPage.delivery,
  eventCategory = Category.deliveryGateFlow,
  hideCoinList,
}: {
  brandId?: number;
  brandName?: string;
  coordinates: Coordinates;
  filterConfig: FILTER_CONFIG_OPTIONS;
  fulfillmentLoading: boolean;
  fulfillmentModeUnavailable?: boolean;
  isDeliveryFulfillmentMode?: boolean;
  isFollowing?: boolean;
  isPickupFulfillmentMode?: boolean;
  mobileClassName?: string;
  outerClassNames?: string;
  permanentFilters?: Filter;
  renderPreFilteredView?: () => React.ReactNode;
  renderNoResults: () => React.ReactNode;
  showEmptyResultsMessaging?: boolean;
  menuPage?: MenuPage;
  eventCategory?: Category;
  hideCoinList?: boolean;
  trackNoResults?: boolean;
}) => {
  const queryParams = useSearchParams();
  const {
    appliedFilters,
    actions: { toggleFilter },
  } = useFilterQueryParams();

  const clearQueryStringKeys = useClearQueryStringKeys();

  const {
    searchMenuItems,
    results: { availableFilters, totalItems, menuItems },
    loading,
  } = useMenuFilterSearch();
  const { publishEvent } = useEventTracker();
  const [menuItemSort, setMenuItemSort] = useState<SortState | null>(null);
  const isValidAddress = useSelector(getIsValidStreetAddress);
  const [modalVisible, setModalVisible] = useState(false);
  const modalReturnRef = createRef<HTMLButtonElement>();

  const q = queryParams?.get("q");
  const boost = queryParams?.get("boost");
  const boostOptionsStrainName = queryParams?.get("boost_options[strain_name]");
  const boostOptionsBrandName = queryParams?.get("boost_options[brand_name]");

  const boostOptions = {
    ...(boostOptionsBrandName && {
      brand_name: boostOptionsBrandName,
    }),
    ...(boostOptionsStrainName && {
      strain_name: boostOptionsStrainName,
    }),
  };

  const publishFilterPanelImpression = {
    action: Action.impression,
    category: eventCategory,
    label: "filter panel",
    nonInteractionEvent: true,
  };

  const filtersApplied = Object.keys(appliedFilters).length > 0;
  const hasFiltersButNoAddress = filtersApplied && !isValidAddress;
  const hasActiveQuery = !!(filtersApplied || q || permanentFilters);

  const searchParams: GetMenuItemsV3Params = useMemo(
    () => ({
      filter_config: filterConfig,
      filters: mapFilterObjectToAPIV2({
        ...appliedFilters,
        // * Do a pickup search when delivery unavailable
        ...(isPickupFulfillmentMode ? PICKUP_FILTERS : {}),
        ...DEFAULT_FILTERS,
        ...(permanentFilters || {}),
      }),
      ...(q ? { q: String(q) } : {}),
      ...(menuItemSort ? menuItemSort : {}),
      ...(hasActiveQuery ? {} : { take: 0 }),
      ...(isDeliveryFulfillmentMode ? DELIVERY_PARAMS : {}),
      ...(boost ? { boost: boost } : {}),
      ...(Object.keys(boostOptions).length > 0
        ? { boost_options: boostOptions }
        : {}),
    }),
    [
      filterConfig,
      appliedFilters,
      isPickupFulfillmentMode,
      permanentFilters,
      q,
      menuItemSort,
      hasActiveQuery,
    ],
  );

  useEffect(() => {
    if (!fulfillmentLoading) {
      searchMenuItems(lat, lon, searchParams);
    }
  }, [lat, lon, searchParams, fulfillmentLoading]);

  const onClickLoadMore = useCallback(() => {
    publishEvent({
      action: Action.click,
      brandId,
      category: eventCategory,
      label: "load more",
    });
    return searchMenuItems(
      lat,
      lon,
      {
        ...searchParams,
        skip: menuItems.length,
      },
      true,
    );
  }, [lat, lon, searchParams, menuItems]);

  const onClearAllClick = (facet?: Facet) => {
    if (!facet) {
      setMenuItemSort(null);
      clearQueryStringKeys(["filter", "boost", "boost_options"]);
      return hasFiltersButNoAddress ? showAddressCaptureModal : undefined;
    }
    toggleFilter(facet.key, facet.value);
    return hasFiltersButNoAddress ? showAddressCaptureModal : undefined;
  };

  const showAddressCaptureModal = () => setModalVisible(true);

  return (
    <div className={outerClassNames}>
      {!isDeliveryFulfillmentMode &&
        Object.keys(appliedFilters).length > 0 &&
        showEmptyResultsMessaging && (
          <h3 className="md:hidden px-lg pb-xs mt-md">
            Try ordering for in-store pickup.
          </h3>
        )}

      <MenuFilters
        allowNonAPISupportedFacets={true}
        availableFilters={availableFilters}
        onClearAllClick={onClearAllClick}
        mobileFilterPanelImpression={publishFilterPanelImpression}
        loading={loading}
        menuPage={menuPage}
        mobileClassName={mobileClassName}
        fulfillmentModeUnavailable={fulfillmentModeUnavailable}
      >
        {hasActiveQuery ? (
          <>
            <AddressCaptureModal
              isVisible={modalVisible}
              onDismissed={() => setModalVisible(false)}
              returnFocusRef={modalReturnRef}
              eventCategory={eventCategory}
            />
            <MenuItemResults
              coordinates={{ lat, lon }}
              isPickupFulfillmentMode={isPickupFulfillmentMode}
              isDeliveryFulfillmentMode={isDeliveryFulfillmentMode}
              menuItems={menuItems as MenuItemCard[]}
              loading={loading}
              onClickLoadMore={onClickLoadMore}
              selectedSort={menuItemSort}
              hasPermanentFilters={!!permanentFilters}
              onChangeSort={setMenuItemSort}
              onShowAddressCaptureModal={showAddressCaptureModal}
              eventCategory={eventCategory}
              totalCount={totalItems}
              {...(brandId && { brandId })}
              {...(brandName && { brandName })}
              {...(isFollowing && { isFollowing })}
              showEmptyResultsMessaging={showEmptyResultsMessaging}
              fulfillmentModeUnavailable={
                !fulfillmentLoading && fulfillmentModeUnavailable
              }
              hideCoinList={hideCoinList}
              renderNoResults={renderNoResults}
            />
          </>
        ) : (
          renderPreFilteredView && renderPreFilteredView()
        )}
      </MenuFilters>
    </div>
  );
};

export default DeliveryMenu;
